/**
 * capturePolicy — pure functions for evaluating expense policies against
 * capture form data before submission. Extracted from useCaptureSession to
 * make policy evaluation testable without rendering React.
 *
 * Responsibilities:
 * - Build the expense data payload from capture form state
 * - Run policy validation via the policy rule engine
 * - Return a structured result with blockers, warnings, and approval info
 */

import { validateExpenseSubmission } from '@/lib/finance/expense/policyRuleEngine';
import type { ExpensePolicyRule } from '@/lib/finance/expense/policyRules';
import type { ChildEntryType } from '../../types';
import { toRecordString } from './captureSchema';
import type { CaptureFormData } from './captureSchema';

export interface CapturePolicyInput {
  selectedEntryType: ChildEntryType;
  formData: CaptureFormData;
  amount: number | undefined;
  policies: unknown;
  clientId: string | null;
  /** Currency detected for this capture (OCR or tenant base currency). */
  currency?: string;
}

export interface CapturePolicyResult {
  canSubmit: boolean;
  requiresApproval: boolean;
  approvalLevel: number;
  blockers: string[];
  warnings: string[];
  appliedPolicies: string[];
}

/**
 * Evaluate expense policies against the current capture form state.
 * Returns a structured result that the caller can use to decide whether
 * to proceed with submission, show warnings, or block entirely.
 */
export function evaluateCapturePolicy(input: CapturePolicyInput): CapturePolicyResult {
  const { selectedEntryType, formData, amount, policies, clientId, currency } = input;
  const formDataRecord = toRecordString(formData);

  const result = validateExpenseSubmission(
    policies as ExpensePolicyRule[],
    {
      amount: amount || 0,
      currency: currency || formDataRecord.currency || undefined,
      category: selectedEntryType === 'purchase'
        ? (formDataRecord.category || formDataRecord.purchase_type || 'other')
        : selectedEntryType,
      vendor: formDataRecord.vendor || formDataRecord.supplier_name || undefined,
      expense_date: formDataRecord.date || new Date().toISOString().split('T')[0],
      expense_type: selectedEntryType,
    },
    { clientId: clientId ? Number(clientId) : 0 },
  );

  return {
    canSubmit: result.canSubmit,
    requiresApproval: result.requiresApproval,
    approvalLevel: result.approvalLevel,
    blockers: result.blockers,
    warnings: result.warnings,
    appliedPolicies: result.appliedPolicies,
  };
}
